//@flow
import * as Immutable from 'immutable';

import { ShiftsInfo } from 'common/models/flow/ShiftsInfo';
import type { ShiftsInfoModelType } from 'common/models/flow/ShiftsInfo';
import { ShiftId } from './ShiftId';
import { Shift } from './Shift';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';

import { DayShift } from './DayShift';

import {
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';

@validatable
class InternalShiftsInfo extends ShiftsInfo {
    static fromJS(json: ShiftsInfoModelType): InternalShiftsInfo {
        const state: Object = Object.assign({}, json);
        state.shiftIds = Immutable.List(state.shiftIds).map(item =>
            ShiftId.fromJS(item)
        );
        state.shifts = Immutable.List(state.shifts).map(item =>
            Shift.fromJS(item)
        );
        state.workingDays = Immutable.List(state.workingDays).map(item =>
            DayShift.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { workingDays } = this;
        if (workingDays) {
            return workingDays.some(dayShift => dayShift.hasValidationErrors());
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { workingDays } = this;
        if (workingDays) {
            workingDays = workingDays.map(dayShift =>
                dayShift.setAllValidationErrors()
            );
        }
        return this.setWorkingDays(workingDays);
    }

    sortWorkingDays() {
        const order = [
            'SUNDAY',
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY'
        ];
        const sortedWorkingDays = this.workingDays.sortBy(
            (workingDayA, workingDayB) => {
                return (
                    order.indexOf(workingDayA.dayOfWeek) -
                    order.indexOf(workingDayB.dayOfWeek)
                );
            }
        );
        return this.setWorkingDays(sortedWorkingDays);
    }

    initializeWeekDays() {
        const days = Object.values(DayOfWeekType);
        const sortedWorkingDays = [];
        days.forEach(day => {
            const slot = this.workingDays.find(dayShift => {
                return dayShift.dayOfWeek === day;
            });
            if (slot) {
                sortedWorkingDays.push(slot);
            } else {
                const shift = DayShift.fromJS({
                    dayOfWeek: day,
                    slot: { start: '', end: '' },
                    breaks: [],
                    startLocation: { latLng: {}, address: '' },
                    endLocation: { latLng: {}, address: '' }
                });
                sortedWorkingDays.push(shift);
            }
        });
        return Immutable.List(sortedWorkingDays);
    }

    get shiftIds(): Immutable.List<ShiftId> {
        return this._state.get('shiftIds');
    }

    get shifts(): Immutable.List<Shift> {
        return this._state.get('shifts');
    }

    get workingDays(): Immutable.List<DayShift> {
        return this._state.get('workingDays');
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        let sanitizedShiftInfo = super.defaultSanitize();
        if (sanitizedShiftInfo) {
            if (sanitizedShiftInfo.workingDays) {
                const sanitizedWorkingDays = sanitizedShiftInfo
                    ? sanitizedShiftInfo.workingDays
                          .map(workingDay => workingDay.defaultSanitize())
                          .filter(workingDay => workingDay.slot)
                    : null;
                sanitizedShiftInfo = sanitizedShiftInfo?.setWorkingDays(
                    sanitizedWorkingDays
                );
            }
            if (
                !sanitizedShiftInfo.shiftIds?.size &&
                !sanitizedShiftInfo.shifts?.size &&
                !sanitizedShiftInfo.workingDays?.size
            ) {
                sanitizedShiftInfo = null;
            }
        }
        return sanitizedShiftInfo;
    }
}

export { InternalShiftsInfo as ShiftsInfo };
